/**
 * Cart functionality for Shopify theme
 * Handles adding products to cart, updating quantities, and cart drawer
 */

class CartManager {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
  }

  bindEvents() {
    // Handle add to cart forms
    document.addEventListener("submit", (e) => {
      if (e.target.matches("[data-product-form]")) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Handle add to cart buttons
    document.addEventListener("click", (e) => {
      if (
        e.target.matches("[data-add-to-cart]") ||
        e.target.closest("[data-add-to-cart]")
      ) {
        e.preventDefault();
        const button = e.target.matches("[data-add-to-cart]")
          ? e.target
          : e.target.closest("[data-add-to-cart]");
        const form = button.closest("form");
        if (form) {
          this.addToCart(form);
        }
      }

      // Handle cart icon clicks
      if (
        e.target.matches("[data-cart-toggle]") ||
        e.target.closest("[data-cart-toggle]")
      ) {
        e.preventDefault();
        this.openCartDrawer();
      }
    });
  }

  async addToCart(form) {
    const formData = new FormData(form);
    const addButton =
      form.querySelector("[data-add-to-cart]") ||
      form.querySelector('[name="add"]');

    // Disable button and show loading state
    if (addButton) {
      addButton.disabled = true;
      const originalText = addButton.textContent;
      addButton.textContent = "ADDING...";

      // Restore button after timeout
      setTimeout(() => {
        addButton.disabled = false;
        addButton.textContent = originalText;
      }, 2000);
    }

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const item = await response.json();
        console.log("Item added to cart:", item);

        // Reload with drawer open param so Liquid re-renders updated cart
        const url = new URL(window.location);
        url.searchParams.set('cart_drawer_open', 'true');
        window.location.href = url.toString();
    } else {
        const error = await response.json();
        throw new Error(error.message || error.description);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showError(error.message);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Update cart count badges
      const cartBadges = document.querySelectorAll(
        ".cart-count, [data-cart-count]"
      );
      cartBadges.forEach((badge) => {
        badge.textContent = cart.item_count;
        if (cart.item_count > 0) {
          badge.style.display = "inline-block";
          badge.classList.remove("hidden");
        } else {
          badge.style.display = "none";
          badge.classList.add("hidden");
        }
      });

      // Update drawer if it exists
      const cartItemCount = document.getElementById("cartItemCount");
      if (cartItemCount) {
        cartItemCount.textContent = cart.item_count;
      }
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  openCartDrawer() {
    if (typeof window.openCartDrawer === "function") {
      window.openCartDrawer();
    } else {
      // Fallback: redirect to cart page
      window.location.href = "/cart";
    }
  }

  showAddToCartFeedback(item) {
    // Create a simple toast notification
    const toast = document.createElement("div");
    toast.className =
      "fixed top-4 right-4 bg-black text-white px-6 py-3 rounded shadow-lg z-50 transform translate-x-full transition-transform duration-300";
    toast.innerHTML = `
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        <span>Added to cart!</span>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = "translateX(0)";
    }, 100);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = "translateX(100%)";
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  showError(message) {
    // Create error toast
    const toast = document.createElement("div");
    toast.className =
      "fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded shadow-lg z-50 transform translate-x-full transition-transform duration-300";
    toast.innerHTML = `
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = "translateX(0)";
    }, 100);

    // Remove after 5 seconds
    setTimeout(() => {
      toast.style.transform = "translateX(100%)";
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 5000);
  }
}

// Utility functions for cart operations
window.CartUtils = {
  async getCart() {
    const response = await fetch("/cart.js");
    return response.json();
  },

  async addItem(variantId, quantity = 1, properties = {}) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: properties,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.description);
    }

    return response.json();
  },

  async updateItem(key, quantity) {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
      }),
    });

    return response.json();
  },

  async removeItem(key) {
    return this.updateItem(key, 0);
  },

  async updateNote(note) {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        note: note,
      }),
    });

    return response.json();
  },

  formatMoney(cents, format) {
    if (!format) {
      format = window.theme?.moneyFormat || "${{amount}}";
    }

    const value = (cents / 100).toFixed(2);
    return format.replace("{{amount}}", value);
  },

  async getVariantInventory(productHandle, variantId) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error("Failed to fetch product data");
      }
      const product = await response.json();
      const variant = product.variants.find((v) => v.id === variantId);
      return variant
        ? {
            inventory_quantity: variant.inventory_quantity || 999999,
            inventory_management: variant.inventory_management,
            inventory_policy: variant.inventory_policy,
            available: variant.available,
          }
        : null;
    } catch (error) {
      console.error("Error fetching variant inventory:", error);
      return null;
    }
  },

  showStockAlert(message) {
    alert(message);
  },
};

// Initialize cart manager when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new CartManager();
});

// Make functions globally available for inline event handlers
window.updateCartQuantity = async function (key, quantity) {
  // Clamp to max available quantity if provided on DOM
  try {
    var itemEl = document.querySelector('[data-cart-item="' + key + '"]');
    if (itemEl) {
      var maxAttr = itemEl.getAttribute("data-max-qty");
      var maxQty = maxAttr ? parseInt(maxAttr, 10) : NaN;
      if (!isNaN(maxQty) && maxQty > 0 && quantity > maxQty) {
        quantity = maxQty;
        try {
          showCartNotice("Only " + maxQty + " in stock for this item.");
        } catch (e) {
          console.warn("Max quantity reached: " + maxQty);
        }
      }
    }

    await CartUtils.updateItem(key, quantity);
    window.location.reload();
  } catch (error) {
    console.error("Error updating cart:", error);
    alert("Error updating cart. Please try again.");
  }
};

window.removeCartItem = async function (key) {
  //   if (!confirm("Remove this item from your cart?")) {
  //     return;
  //   }

  try {
    await CartUtils.removeItem(key);
    window.location.reload();
  } catch (error) {
    console.error("Error removing item:", error);
    alert("Error removing item. Please try again.");
  }
};

window.updateCartNote = async function (note) {
  try {
    await CartUtils.updateNote(note);
  } catch (error) {
    console.error("Error updating cart note:", error);
  }
};

// Drawer-specific functions
window.updateDrawerQuantity = async function (key, quantity) {
  try {
    // Get current cart to find the item and check current quantities
    const cart = await CartUtils.getCart();
    const cartItem = cart.items.find((item) => item.key === key);

    if (!cartItem) {
      console.error("Item not found in cart");
      return;
    }

    // Fetch fresh inventory data for this variant
    const variantInventory = await CartUtils.getVariantInventory(
      cartItem.product_handle,
      cartItem.variant_id
    );

    if (
      variantInventory &&
      variantInventory.inventory_management === "shopify" &&
      variantInventory.inventory_policy === "deny"
    ) {
      // Check if requested quantity exceeds available stock
      if (quantity > variantInventory.inventory_quantity) {
        quantity = variantInventory.inventory_quantity;
        if (variantInventory.inventory_quantity > 0) {
          CartUtils.showStockAlert(
            `Only ${variantInventory.inventory_quantity} in stock for this item.`
          );
        } else {
          CartUtils.showStockAlert("This item is out of stock.");
          return;
        }
      }
    }

    await CartUtils.updateItem(key, quantity);

    // Refresh cart count and drawer content
    const updatedCart = await CartUtils.getCart();
    updateCartDrawerUI(updatedCart);

    // Update button states after refresh
    updateDrawerButtonStates(updatedCart);

    if (typeof refreshCartDrawer === "function") {
      refreshCartDrawer();
    }
  } catch (error) {
    console.error("Error updating cart:", error);
    CartUtils.showStockAlert("Error updating cart. Please try again.");
  }
};

// Helper function to update drawer button states based on inventory
window.updateDrawerButtonStates = async function (cart) {
  for (const item of cart.items) {
    const variantInventory = await CartUtils.getVariantInventory(
      item.product_handle,
      item.variant_id
    );

    if (
      variantInventory &&
      variantInventory.inventory_management === "shopify" &&
      variantInventory.inventory_policy === "deny"
    ) {
      const itemEl = document.querySelector(`[data-cart-item="${item.key}"]`);
      if (itemEl) {
        // Disable + button if at max inventory
        const plusBtn = itemEl.querySelector('button[onclick*="plus"]');
        if (plusBtn) {
          if (item.quantity >= variantInventory.inventory_quantity) {
            plusBtn.disabled = true;
          } else {
            plusBtn.disabled = false;
          }
        }

        // Disable - button if at min
        const minusBtn = itemEl.querySelector('button[onclick*="minus"]');
        if (minusBtn) {
          minusBtn.disabled = item.quantity <= 1;
        }
      }
    }
  }
};

window.updateCartDrawerUI = function (cart) {
  // Update item count
  const cartItemCount = document.getElementById("cartItemCount");
  if (cartItemCount) {
    cartItemCount.textContent = cart.item_count;
  }

  // Update total
  const cartTotal = document.getElementById("cartTotal");
  if (cartTotal) {
    cartTotal.textContent = CartUtils.formatMoney(cart.total_price);
  }

  // Update cart count badges
  const cartBadges = document.querySelectorAll(
    ".cart-count, [data-cart-count]"
  );
  cartBadges.forEach((badge) => {
    badge.textContent = cart.item_count;
    if (cart.item_count > 0) {
      badge.style.display = "inline-block";
      badge.classList.remove("hidden");
    } else {
      badge.style.display = "none";
      badge.classList.add("hidden");
    }
  });

  // If cart is empty, show empty state or close drawer
  if (cart.item_count === 0) {
    setTimeout(() => {
      if (typeof closeCartDrawer === "function") {
        closeCartDrawer();
      }
      window.location.reload();
    }, 1000);
  }
};
